<template>
  <v-dialog v-model="dialog" max-width="1100" @click:outside="$emit('close')">
    <v-card>
      <v-card-title class="headline">{{ title }}</v-card-title>

      <v-card-text>
        <div v-if="loading" class="text-center py-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <p v-else-if="segments.length == 0" class="mb-0 fs-16 text-center">No hay recorrido disponible para esta ruta.</p>
        <div v-else>
          <GmapMap
            v-if="dialog"
            ref="mapRef"
            :center="{ lat: -34.6, lng: -58.4 }"
            :zoom="10"
            :options="{ mapTypeControl: false, streetViewControl: false, fullscreenControl: true }"
            style="width: 100%; height: 550px"
          />

          <p v-if="segments.length > 1" class="fs-14 mt-3 mb-2">
            El recorrido se divide en {{ segments.length }} etapas por el límite de paradas de Google Maps.
          </p>
          <div class="d-flex flex-wrap mt-3">
            <v-btn
              v-for="(segment, index) in segments"
              :key="segment.segment"
              :href="segment.googleMapsUrl"
              target="_blank"
              rel="noopener noreferrer"
              :color="getColor(index)"
              outlined
              small
              class="mr-2 mb-2"
            >
              <v-icon left small>open_in_new</v-icon>
              {{ segments.length > 1 ? "Abrir etapa " + segment.segment + " en Google Maps" : "Abrir en Google Maps" }}
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="red darken-1" text @click="$emit('close')">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const COLORS = ["#1976d2", "#e53935", "#43a047", "#fb8c00", "#8e24aa", "#00897b"];

export default {
  props: {
    dialog: Boolean,
    loading: Boolean,
    title: { type: String, default: "Recorrido" },
    segments: { type: Array, default: () => [] },
  },
  data() {
    return {
      overlays: [],
    };
  },
  watch: {
    segments() {
      this.$nextTick(this.drawRoute);
    },
    loading() {
      this.$nextTick(this.drawRoute);
    },
    dialog(value) {
      if (value) this.$nextTick(this.drawRoute);
      else this.clearOverlays();
    },
  },
  methods: {
    getColor(index) {
      return COLORS[index % COLORS.length];
    },
    clearOverlays() {
      this.overlays.forEach((overlay) => overlay.setMap(null));
      this.overlays = [];
    },
    parsePoint(value) {
      const parts = value.split(",").map((v) => parseFloat(v));
      if (parts.length == 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return { lat: parts[0], lng: parts[1] };
      }
      return value;
    },
    parseSegmentUrl(url) {
      const params = new URL(url).searchParams;
      const waypoints = params.get("waypoints");
      return {
        origin: this.parsePoint(params.get("origin")),
        destination: this.parsePoint(params.get("destination")),
        waypoints: waypoints ? waypoints.split("|").map((w) => this.parsePoint(w.trim())) : [],
      };
    },
    drawRoute() {
      if (!this.dialog || this.loading || this.segments.length == 0 || !this.$refs.mapRef) return;

      this.$refs.mapRef.$mapPromise.then((map) => {
        this.clearOverlays();
        const google = window.google;
        const bounds = new google.maps.LatLngBounds();
        const directionsService = new google.maps.DirectionsService();

        this.segments.forEach((segment, index) => {
          let route;
          try {
            route = this.parseSegmentUrl(segment.googleMapsUrl);
          } catch (e) {
            console.error("URL de recorrido invalida:", segment.googleMapsUrl);
            return;
          }
          const color = this.getColor(index);
          const points = [route.origin, ...route.waypoints, route.destination];
          points.forEach((p) => {
            if (typeof p == "object") bounds.extend(p);
          });

          directionsService.route(
            {
              origin: route.origin,
              destination: route.destination,
              waypoints: route.waypoints.map((location) => ({ location, stopover: true })),
              travelMode: google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status == "OK") {
                const renderer = new google.maps.DirectionsRenderer({
                  map,
                  directions: result,
                  preserveViewport: true,
                  polylineOptions: { strokeColor: color, strokeWeight: 5, strokeOpacity: 0.8 },
                });
                this.overlays.push(renderer);
              } else {
                // Sin Directions API: se dibuja una linea recta entre las paradas
                console.warn("DirectionsService:", status);
                this.drawFallback(map, points, color);
              }
            }
          );
        });

        if (!bounds.isEmpty()) map.fitBounds(bounds);
      });
    },
    drawFallback(map, points, color) {
      const google = window.google;
      const latLngPoints = points.filter((p) => typeof p == "object");
      this.overlays.push(new google.maps.Polyline({ map, path: latLngPoints, strokeColor: color, strokeWeight: 4, strokeOpacity: 0.8 }));
      latLngPoints.forEach((position, i) => {
        this.overlays.push(new google.maps.Marker({ map, position, label: String(i + 1) }));
      });
    },
  },
};
</script>
